import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useUser } from '../context/UserContext';
import ShinyText from '../components/ShinyText';
import { 
  Trophy, 
  Crown, 
  Star, 
  TrendingUp, 
  Medal,
  Target,
  Zap,
  Calendar,
  Clock
} from 'lucide-react';

const Leaderboard = () => {
  const { user, loading } = useUser();
  const [selectedCategory, setSelectedCategory] = useState('overall');

  // Show loading only if we're actually loading and there's a token
  if (loading && localStorage.getItem('token')) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-gray-600 dark:text-gray-300">Loading...</p>
        </div>
      </div>
    );
  }

  // If no user and no token, show the leaderboard without user-specific features
  const isAuthenticated = !!user;

  // Mock leaderboard data with ranks and enhanced XP tracking
  const leaderboardData = {
    overall: [
      { rank: 1, username: 'DebateMaster2024', level: 25, xp: 25000, crowns: 89, trophies: 15, streak: 45, winRate: 92, totalDebates: 156 },
      { rank: 2, username: 'LogicQueen', level: 23, xp: 23000, crowns: 76, trophies: 12, streak: 38, winRate: 89, totalDebates: 142 },
      { rank: 3, username: 'ArgumentAce', level: 22, xp: 22000, crowns: 71, trophies: 11, streak: 42, winRate: 87, totalDebates: 138 },
      { rank: 4, username: 'RhetoricRex', level: 21, xp: 21000, crowns: 68, trophies: 10, streak: 35, winRate: 85, totalDebates: 125 },
      { rank: 5, username: 'CriticalThinker', level: 20, xp: 20000, crowns: 65, trophies: 9, streak: 33, winRate: 83, totalDebates: 118 },
      { rank: 6, username: 'PersuasionPro', level: 19, xp: 19000, crowns: 62, trophies: 8, streak: 30, winRate: 81, totalDebates: 112 },
      { rank: 7, username: 'FallacyFinder', level: 18, xp: 18000, crowns: 59, trophies: 7, streak: 28, winRate: 79, totalDebates: 105 },
      { rank: 8, username: 'EvidenceExpert', level: 17, xp: 17000, crowns: 56, trophies: 6, streak: 25, winRate: 77, totalDebates: 98 },
      { rank: 9, username: 'RebuttalRuler', level: 16, xp: 16000, crowns: 53, trophies: 5, streak: 22, winRate: 75, totalDebates: 92 },
      { rank: 10, username: 'DebateChampion2024', level: 15, xp: 12500, crowns: 45, trophies: 3, streak: 12, winRate: 78, totalDebates: 85 }
    ],
    weekly: [
      { rank: 1, username: 'LogicQueen', level: 23, xp: 23000, crowns: 76, trophies: 12, streak: 38, winRate: 89, weeklyXP: 2500, weeklyDebates: 12 },
      { rank: 2, username: 'ArgumentAce', level: 22, xp: 22000, crowns: 71, trophies: 11, streak: 42, winRate: 87, weeklyXP: 2200, weeklyDebates: 11 },
      { rank: 3, username: 'DebateMaster2024', level: 25, xp: 25000, crowns: 89, trophies: 15, streak: 45, winRate: 92, weeklyXP: 2000, weeklyDebates: 10 },
      { rank: 4, username: 'RhetoricRex', level: 21, xp: 21000, crowns: 68, trophies: 10, streak: 35, winRate: 85, weeklyXP: 1800, weeklyDebates: 9 },
      { rank: 5, username: 'CriticalThinker', level: 20, xp: 20000, crowns: 65, trophies: 9, streak: 33, winRate: 83, weeklyXP: 1600, weeklyDebates: 8 },
      { rank: 6, username: 'PersuasionPro', level: 19, xp: 19000, crowns: 62, trophies: 8, streak: 30, winRate: 81, weeklyXP: 1400, weeklyDebates: 7 },
      { rank: 7, username: 'FallacyFinder', level: 18, xp: 18000, crowns: 59, trophies: 7, streak: 28, winRate: 79, weeklyXP: 1200, weeklyDebates: 6 },
      { rank: 8, username: 'EvidenceExpert', level: 17, xp: 17000, crowns: 56, trophies: 6, streak: 25, winRate: 77, weeklyXP: 1000, weeklyDebates: 5 },
      { rank: 9, username: 'RebuttalRuler', level: 16, xp: 16000, crowns: 53, trophies: 5, streak: 22, winRate: 75, weeklyXP: 800, weeklyDebates: 4 },
      { rank: 10, username: 'DebateChampion2024', level: 15, xp: 12500, crowns: 45, trophies: 3, streak: 12, winRate: 78, weeklyXP: 600, weeklyDebates: 3 }
    ],
    monthly: [
      { rank: 1, username: 'DebateMaster2024', level: 25, xp: 25000, crowns: 89, trophies: 15, streak: 45, winRate: 92, monthlyXP: 8000, monthlyDebates: 45 },
      { rank: 2, username: 'LogicQueen', level: 23, xp: 23000, crowns: 76, trophies: 12, streak: 38, winRate: 89, monthlyXP: 7500, monthlyDebates: 42 },
      { rank: 3, username: 'ArgumentAce', level: 22, xp: 22000, crowns: 71, trophies: 11, streak: 42, winRate: 87, monthlyXP: 7000, monthlyDebates: 38 },
      { rank: 4, username: 'RhetoricRex', level: 21, xp: 21000, crowns: 68, trophies: 10, streak: 35, winRate: 85, monthlyXP: 6500, monthlyDebates: 35 },
      { rank: 5, username: 'CriticalThinker', level: 20, xp: 20000, crowns: 65, trophies: 9, streak: 33, winRate: 83, monthlyXP: 6000, monthlyDebates: 32 },
      { rank: 6, username: 'PersuasionPro', level: 19, xp: 19000, crowns: 62, trophies: 8, streak: 30, winRate: 81, monthlyXP: 5500, monthlyDebates: 28 },
      { rank: 7, username: 'FallacyFinder', level: 18, xp: 18000, crowns: 59, trophies: 7, streak: 28, winRate: 79, monthlyXP: 5000, monthlyDebates: 25 },
      { rank: 8, username: 'EvidenceExpert', level: 17, xp: 17000, crowns: 56, trophies: 6, streak: 25, winRate: 77, monthlyXP: 4500, monthlyDebates: 22 },
      { rank: 9, username: 'RebuttalRuler', level: 16, xp: 16000, crowns: 53, trophies: 5, streak: 22, winRate: 75, monthlyXP: 4000, monthlyDebates: 18 },
      { rank: 10, username: 'DebateChampion2024', level: 15, xp: 12500, crowns: 45, trophies: 3, streak: 12, winRate: 78, monthlyXP: 3500, monthlyDebates: 15 }
    ]
  };

  const categories = [
    { id: 'overall', name: 'Overall', icon: Trophy, description: 'All-time rankings' },
    { id: 'weekly', name: 'This Week', icon: TrendingUp, description: 'Weekly performance' },
    { id: 'monthly', name: 'This Month', icon: Calendar, description: 'Monthly achievements' }
  ];

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1: return (
        <div className="flex items-center space-x-2">
          <Medal className="w-6 h-6 text-yellow-500" />
          <ShinyText text={`#${rank}`} speed={4} className="text-yellow-600 font-bold" />
        </div>
      );
      case 2: return (
        <div className="flex items-center space-x-2">
          <Medal className="w-6 h-6 text-gray-400" />
          <ShinyText text={`#${rank}`} speed={4} className="text-gray-600 font-bold" />
        </div>
      );
      case 3: return (
        <div className="flex items-center space-x-2">
          <Medal className="w-6 h-6 text-amber-600" />
          <ShinyText text={`#${rank}`} speed={4} className="text-amber-700 font-bold" />
        </div>
      );
      default: return <span className="text-lg font-bold text-gray-600">#{rank}</span>;
    }
  };

  const formatXP = (xp) => {
    if (xp >= 1000) {
      return `${(xp / 1000).toFixed(1)}k`;
    }
    return xp.toString();
  };

  const currentData = leaderboardData[selectedCategory];
  const currentUser = isAuthenticated ? currentData.find(leaderboardUser => leaderboardUser.username === user?.username) : null;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-24 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Leaderboard</h1>
          <p className="text-xl text-gray-600 dark:text-gray-300">
            Compete with the best debaters and track your progress
          </p>
        </motion.div>

        {/* Enhanced Category Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex justify-center mb-8"
        >
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-1 shadow-lg border border-gray-200 dark:border-gray-700">
            <div className="flex space-x-1">
              {categories.map((category) => {
                const Icon = category.icon;
                const isSelected = selectedCategory === category.id;
                return (
                  <motion.button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 flex items-center space-x-2 ${
                      isSelected
                        ? 'bg-gradient-to-r from-primary-600 to-secondary-600 text-white shadow-lg transform scale-105'
                        : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Icon className={`w-4 h-4 ${isSelected ? 'text-white' : 'text-gray-500 dark:text-gray-400'}`} />
                    <span className="font-semibold">{category.name}</span>
                  </motion.button>
                );
              })}
            </div>
          </div>
        </motion.div>

        {/* Enhanced Current User Stats */}
        {currentUser && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-8"
          >
            <div className="bg-gradient-to-r from-primary-600 via-secondary-600 to-primary-700 rounded-xl p-6 text-white shadow-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                    <Crown className="w-8 h-8" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">Your Ranking</h3>
                    <p className="text-gray-200">Keep up the great work!</p>
                    <div className="flex items-center space-x-4 mt-2">
                      <div className="flex items-center space-x-1">
                        <Zap className="w-4 h-4" />
                        <span className="text-sm">{formatXP(currentUser.xp)} XP</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Crown className="w-4 h-4" />
                        <span className="text-sm">{currentUser.crowns} Crowns</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-4xl font-bold">#{currentUser.rank}</div>
                  <div className="text-sm text-gray-200">out of {currentData.length}</div>
                  <div className="mt-2 text-xs text-gray-300">
                    {selectedCategory === 'weekly' && `${currentUser.weeklyXP} XP this week`}
                    {selectedCategory === 'monthly' && `${currentUser.monthlyXP} XP this month`}
                    {selectedCategory === 'overall' && `${currentUser.totalDebates} total debates`}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Enhanced Leaderboard Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-200 dark:border-gray-700"
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white">Rank</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white">User</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white">Level</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white">XP</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white">Crowns</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white">Trophies</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white">Streak</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white">Win Rate</th>
                  {selectedCategory !== 'overall' && (
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white">
                      {selectedCategory === 'weekly' ? 'Weekly XP' : 'Monthly XP'}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {currentData.map((leaderboardUser, index) => (
                  <motion.tr
                    key={leaderboardUser.username}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                      isAuthenticated && leaderboardUser.username === user?.username ? 'bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-primary-900/20 dark:to-secondary-900/20 border-l-4 border-primary-500' : ''
                    }`}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        {getRankIcon(leaderboardUser.rank)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full flex items-center justify-center text-white font-semibold text-sm shadow-md">
                          {leaderboardUser.username.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900 dark:text-white">
                            {leaderboardUser.rank <= 3 ? (
                              <ShinyText text={leaderboardUser.username} speed={4} className="text-gray-900 dark:text-white" />
                            ) : (
                              leaderboardUser.username
                            )}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center space-x-1">
                            <span>Rank #{leaderboardUser.rank}</span>
                            {isAuthenticated && leaderboardUser.username === user?.username && (
                              <span className="bg-primary-100 dark:bg-primary-900/20 text-primary-800 dark:text-primary-300 px-2 py-1 rounded-full text-xs font-medium">
                                You
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <Target className="w-4 h-4 text-primary-600" />
                        <span className="font-semibold text-gray-900 dark:text-white">{leaderboardUser.level}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <Zap className="w-4 h-4 text-yellow-500" />
                        <span className="font-semibold text-gray-900 dark:text-white">{formatXP(leaderboardUser.xp)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <Crown className="w-4 h-4 text-yellow-600" />
                        <span className="font-semibold text-gray-900 dark:text-white">{leaderboardUser.crowns}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <Trophy className="w-4 h-4 text-amber-600" />
                        <span className="font-semibold text-gray-900 dark:text-white">{leaderboardUser.trophies}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <Star className="w-4 h-4 text-orange-500" />
                        <span className="font-semibold text-gray-900 dark:text-white">{leaderboardUser.streak}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <TrendingUp className="w-4 h-4 text-green-600" />
                        <span className="font-semibold text-gray-900 dark:text-white">{leaderboardUser.winRate}%</span>
                      </div>
                    </td>
                    {selectedCategory !== 'overall' && (
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <Clock className="w-4 h-4 text-blue-600" />
                          <span className="font-semibold text-gray-900 dark:text-white">
                            {formatXP(selectedCategory === 'weekly' ? leaderboardUser.weeklyXP : leaderboardUser.monthlyXP)}
                          </span>
                        </div>
                      </td>
                    )}
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Leaderboard; 